import Raven from 'raven-js';
import { ANY, ARCHIVED, DELIVERY_METHOD_NAMES, DELIVERY_METHOD_NAME_MAP } from '../constants';
import { getApiConfigs, captureUndefinedPortalError, getExtraQueryParamsForArchive, filterTypesForApiCall, apiClientWithMiddleware } from './utils';
import { debug } from '../utils/DebugUtil';
import { isTrashTab, isUnreadTab } from '../channels/sidebar/utils/TabUtils';
const NOTIFICATION_STATION_API_V1_URL = 'notification-station/general/v1/notifications';
const NOTIFICATION_STATION_API_V2_URL = 'notification-station/general/v2/notifications';
export const NOTIFICATION_STATION_API_V3_URL = 'notification-station/general/v3/notifications';
const TURN_OFF_API_PREFERENCE = `notification/preferences/v2/preferences/turn-off/preference`;
const TURN_ON_API_PREFERENCE = `notification/preferences/v2/preferences/turn-on/preference`;
const TURN_OFF_SOUND_URL = 'notification/preferences/v2/preferences/turn-off/sound';
const NOTIFICATION_TYPE_URL = 'notification-station/general/v1/info/types';
export function markAsDelivered({
  notificationId,
  deliveryMethod
}) {
  const url = `${NOTIFICATION_STATION_API_V1_URL}/delivered/${notificationId}/${deliveryMethod}`;
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).put(url, queryConfig).catch(error => {
    if (error.status !== 0) {
      debug(`Error while marking ${deliveryMethod} notification id:${notificationId} as delivered`, error);
      throw error;
    }
  });
}
export function markNotificationAsReadApi({
  notificationId,
  deliveryMethod
}) {
  const url = `${NOTIFICATION_STATION_API_V1_URL}/mark/${notificationId}`;
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).put(url, {
    query: {
      portalId,
      deliveryMethod
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while marking ${deliveryMethod} notification id:${notificationId} as read`, error);
      throw error;
    }
    return Promise.resolve(null);
  });
}
export function markNotificationsAsRead(notifications) {
  return Promise.all(notifications.map(notification => markNotificationAsReadApi({
    notificationId: notification.id,
    deliveryMethod: DELIVERY_METHOD_NAMES.SIDEBAR
  })));
}
export function markAsSeen(notifications) {
  const unreadIDs = notifications.filter(({
    read
  }) => !read).map(({
    id
  }) => id);
  if (!unreadIDs.length) {
    return Promise.resolve(false);
  }
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(false);
  }
  return apiClientWithMiddleware(clientConfig).put(`${NOTIFICATION_STATION_API_V1_URL}/seen/id`, {
    query: {
      portalId
    },
    data: unreadIDs
  }).then(() => true).catch(error => {
    if (error.status !== 0) {
      debug(`Error while marking notifications as read`, error);
      throw error;
    }
    return false;
  });
}
export const turnOffNotification = ({
  id,
  deliveryMethod
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).postWithResponse(`${TURN_OFF_API_PREFERENCE}?portalId=${portalId}`, {
    data: {
      notificationId: id,
      deliveryMethod: DELIVERY_METHOD_NAME_MAP[deliveryMethod]
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while turning off ${deliveryMethod} notification (id: ${id})`, error);
      throw error;
    }
  });
};
export const turnOnNotification = ({
  id,
  deliveryMethod
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).postWithResponse(`${TURN_ON_API_PREFERENCE}?portalId=${portalId}`, {
    data: {
      notificationId: id,
      deliveryMethod: DELIVERY_METHOD_NAME_MAP[deliveryMethod]
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while turning on ${deliveryMethod} notification (id: ${id})`, error);
      throw error;
    }
  });
};
export const turnOffNotificationSound = ({
  id,
  deliveryMethod
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).postWithResponse(`${TURN_OFF_SOUND_URL}?portalId=${portalId}`, {
    data: {
      notificationId: id,
      deliveryMethod: DELIVERY_METHOD_NAME_MAP[deliveryMethod]
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while turning sound off for ${deliveryMethod} notification (id: ${id})`, error);
      throw error;
    }
  });
};
export const getNotificationsList = ({
  filterOptions = [],
  before,
  beforeId
}) => {
  const {
    queryConfig,
    clientConfig,
    locale
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve({
      hasMore: false,
      notifications: []
    });
  }
  const types = filterTypesForApiCall(filterOptions);
  const archived = filterOptions.includes(ARCHIVED) ? ARCHIVED : 'NOT_ARCHIVED';
  return apiClientWithMiddleware(clientConfig).get(`${NOTIFICATION_STATION_API_V2_URL}/list?`, {
    query: {
      portalId,
      archived,
      types,
      before,
      beforeId,
      locale
    }
  }).then(response => {
    // This is a temporary fix for the API response to remove the typo, will remove this once the v3 API is used
    response.notifications.forEach(notification => {
      if (notification.isPreferenceSetable !== undefined) {
        notification.isPreferenceSettable = notification.isPreferenceSetable;
        delete notification.isPreferenceSetable;
      }
    });
    return response;
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error fetching notifications list`, error);
    }
    throw error;
  });
};
export const getV3NotificationsList = ({
  filterOption,
  beforeId,
  sidebarTabState: tabState
}) => {
  const {
    queryConfig,
    clientConfig,
    locale
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  const showUnreadOnly = isUnreadTab(tabState);
  const showArchivedOnly = isTrashTab(tabState);
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve({
      hasMore: false,
      notifications: []
    });
  }
  const url = isTrashTab(tabState) ? '/list-deleted?' : '/list?';
  const queryParams = Object.assign({
    portalId,
    types: filterOption !== ANY ? [filterOption] : [],
    beforeId,
    locale
  }, showArchivedOnly ? {} : {
    showUnreadOnly
  });
  return apiClientWithMiddleware(clientConfig).get(`${NOTIFICATION_STATION_API_V3_URL}${url}`, {
    query: queryParams
  }).catch(error => {
    if (error.status === 404) {
      return apiClientWithMiddleware(clientConfig).get(`${NOTIFICATION_STATION_API_V1_URL}/list?`, {
        query: Object.assign({}, queryParams, {
          archived: ARCHIVED
        })
      });
    }
    if (error.status !== 0) {
      debug(`Error fetching unread notifications list`, error);
    }
    throw error;
  });
};
/**
 * Fetch types for the old sidebar
 * @deprecated use getV2NotificationTypes for additional locale support
 */
export const getNotificationTypes = () => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve([]);
  }
  return apiClientWithMiddleware(clientConfig).get(`${NOTIFICATION_TYPE_URL}?portalId=${queryConfig.query.portalId}&filter=USER_ONLY`).catch(error => {
    if (error.status !== 0) {
      debug(`Error fetching notifications types`, error);
    }
    return [];
  });
};
export const getV2NotificationTypes = () => {
  const {
    queryConfig,
    clientConfig,
    locale
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve([]);
  }
  return apiClientWithMiddleware(clientConfig).get(`notification-station/general/v2/info/types`, {
    query: {
      filter: 'USER_ONLY',
      locale,
      portalId
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error fetching notifications types`, error);
    }
    return [];
  });
};
export const archiveOrUnarchiveAllNotifications = ({
  archive,
  types = []
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  const filteredTypes = filterTypesForApiCall(types);
  const archiveString = archive ? 'archive' : 'unarchive';
  const extraParams = getExtraQueryParamsForArchive();
  const options = {
    query: Object.assign({
      portalId,
      deliveryMethod: 'NOTIFICATION_SIDEBAR',
      types: filteredTypes
    }, extraParams)
  };
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve([]);
  }
  return apiClientWithMiddleware(clientConfig).put(`${NOTIFICATION_STATION_API_V1_URL}/${archiveString}/all`, options).then(data => {
    return data;
  }).catch(() => {
    return [];
  });
};
export const archiveOrUnarchiveNotifications = ({
  notificationIds,
  archive
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  const archiveString = archive ? 'archive' : 'unarchive';
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve();
  }
  return apiClientWithMiddleware(clientConfig).put(`${NOTIFICATION_STATION_API_V1_URL}/${archiveString}?portalId=${portalId}`, {
    data: {
      notificationIds
    }
  });
};
export const markAllNotificationsAsRead = ({
  types = []
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve([]);
  }
  const filteredTypes = filterTypesForApiCall(types);
  return apiClientWithMiddleware(clientConfig).put(`${NOTIFICATION_STATION_API_V1_URL}/mark/all`, {
    query: {
      portalId,
      deliveryMethod: 'NOTIFICATION_SIDEBAR',
      types: filteredTypes
    }
  });
};
export const getUnseenNotificationCount = async () => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return 0;
  }
  try {
    const response = await apiClientWithMiddleware(clientConfig).getWithResponse(`${NOTIFICATION_STATION_API_V1_URL}/count`, {
      query: {
        portalId
      }
    });
    return response.data;
  } catch (e) {
    if (e instanceof Error) {
      Raven.captureException(e);
    }
    return 0;
  }
};
export const clearUnseenCount = async () => {
  const url = `${NOTIFICATION_STATION_API_V1_URL}/clear-bell-counter`;
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return;
  }
  try {
    await apiClientWithMiddleware(clientConfig).put(url, queryConfig);
  } catch (e) {
    if (e instanceof Error) {
      Raven.captureException(e);
    }
  }
};
export const batchActionOnSomeSidebarNotifications = ({
  action,
  notificationIds
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const {
    query
  } = queryConfig;
  return apiClientWithMiddleware(clientConfig).post(`${NOTIFICATION_STATION_API_V3_URL}/batch/action`, {
    data: {
      action,
      includedIds: notificationIds
    },
    query
  });
};
export const batchActionOnAllSidebarNotifications = ({
  action,
  notificationIds,
  sidebarState,
  filterType
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const {
    query
  } = queryConfig;
  return apiClientWithMiddleware(clientConfig).post(`${NOTIFICATION_STATION_API_V3_URL}/all/action`, {
    data: {
      action,
      excludedIds: notificationIds,
      sidebarState,
      types: [filterType]
    },
    query
  });
};
export const singleActionOnNotification = ({
  action,
  notificationId
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const {
    query
  } = queryConfig;
  return apiClientWithMiddleware(clientConfig).post(`${NOTIFICATION_STATION_API_V3_URL}/${notificationId}/action`, {
    data: {
      action,
      notificationId
    },
    query
  });
};
export const getUnreadCount = () => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const url = `${NOTIFICATION_STATION_API_V3_URL}/count`;
  return apiClientWithMiddleware(clientConfig).get(url, {
    query: {
      portalId: queryConfig.query.portalId,
      showUnreadOnly: true
    }
  });
};